import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useBlocks } from '../hooks/useBlocks';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useCampaignPool } from '../hooks/useCampaignPool';
import { useMailboxes } from '../hooks/useMailboxes';
import type { CampaignDetail } from '../outreachTypes';
import { outreachCampaignPath } from '../outreachPaths';
import { shortDate } from '../outreachFormat';
import { canSendFrom } from './MailboxPick';
import { rampReachesOn } from './firstWeekPlan';
import { ModeCards } from './ModeCards';
import { Recap } from './Recap';
import { StartBar } from './StartBar';
import { TimingRows } from './TimingRows';
import type { BuilderStep } from './builderSteps';
import { startMessages as messages } from './startMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepStartProps {
  accountId: number;
  campaign: CampaignDetail;
  readOnly: boolean;
  /** A running campaign ("Edit setup"): "Save changes" goes back to its page; nothing starts. */
  isEditing?: boolean;
  onStep: (step: BuilderStep) => void;
}

/** The message of a refused start: no mailbox that can send, or a general failure. */
function startProblem(error: unknown): 'noMailbox' | 'failed' {
  const code = (error as { data?: { code?: unknown } } | null)?.data?.code;
  return code === 'PRECONDITION_FAILED' ? 'noMailbox' : 'failed';
}

/**
 * Step 5 "Start" (design outreach-v2 f1, f2): how pitches go out, the timing, the recap and "Start
 * pitching" (`campaigns.start`). Then the page goes to the live campaign. A running campaign
 * ("Edit setup") shows "Save changes" instead and starts nothing.
 */
const StepStartComponent: React.FC<StepStartProps> = ({ accountId, campaign, readOnly, isEditing = false, onStep }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showSuccess } = useShowToast();
  const { startCampaign, isChangingState } = useCampaignActions(accountId);
  const { pool } = useCampaignPool(accountId, campaign.campaignUuid);
  const { blocks } = useBlocks(accountId);
  const { mailboxes } = useMailboxes(accountId);
  const [problem, setProblem] = useState<'noMailbox' | 'failed' | null>(null);
  const from = useMemo(
    () =>
      mailboxes.filter((mailbox) => campaign.mailboxUuids.includes(mailbox.mailboxUuid) && canSendFrom(mailbox.health)),
    [campaign.mailboxUuids, mailboxes]
  );
  const fitNow = useMemo(
    () => (pool === null ? campaign.poolTotal : campaign.kinds.reduce((sum, kind) => sum + pool.byKind[kind], 0)),
    [campaign.kinds, campaign.poolTotal, pool]
  );
  // The mailbox with the largest room today sets the warm-up line.
  const warming = useMemo(
    () => from.filter((mailbox) => mailbox.warmup.warming).sort((a, b) => b.warmup.todayCap - a.warmup.todayCap)[0],
    [from]
  );
  const warmupCap =
    warming !== undefined &&
    from.every((mailbox) => mailbox.warmup.warming) &&
    warming.warmup.todayCap < campaign.dailyRate
      ? warming.warmup.todayCap
      : null;

  const start = useCallback(() => {
    setProblem(null);
    startCampaign(campaign.campaignUuid)
      .then((result) => {
        const toast = {
          today: () => intl.formatMessage(messages.startedToday, { count: result.pickedToday }),
          tomorrow: () => intl.formatMessage(messages.startedPicked, { count: result.pickedToday }),
          nextBatch: () => intl.formatMessage(messages.startedTonight),
        };
        showSuccess(toast[result.firstBatch]());
        // eslint-disable-next-line no-void
        void navigate(outreachCampaignPath(campaign.campaignUuid));
      })
      .catch((error: unknown) => setProblem(startProblem(error)));
  }, [campaign.campaignUuid, intl, navigate, showSuccess, startCampaign]);

  // Every step saved on change (`campaigns.update`): "Save changes" only confirms and goes back.
  const save = useCallback(() => {
    showSuccess(intl.formatMessage(messages.saved));
    // eslint-disable-next-line no-void
    void navigate(outreachCampaignPath(campaign.campaignUuid));
  }, [campaign.campaignUuid, intl, navigate, showSuccess]);

  return (
    <>
      <ModeCards accountId={accountId} campaign={campaign} readOnly={readOnly} />
      <TimingRows accountId={accountId} campaign={campaign} readOnly={readOnly} />
      {warmupCap !== null && warming !== undefined && (
        <div className={shared.alert} data-testid="builder-warmup-note">
          <FontAwesomeIcon icon={faCircleInfo} className={shared.alertIcon} />
          <span>
            {intl.formatMessage(messages.warmupNote, {
              address: warming.address,
              day: warming.warmup.day + 1,
              song: campaign.songTitle,
              cap: warmupCap,
              rate: campaign.dailyRate,
              date: shortDate(
                intl,
                rampReachesOn(new Date(warming.connectedAt), campaign.dailyRate)?.toISOString() ?? null
              ),
            })}
          </span>
        </div>
      )}
      <Recap
        campaign={campaign}
        fitNow={fitNow}
        blocks={blocks.length}
        from={from}
        warmupCap={warmupCap}
        onEdit={onStep}
      />
      {problem !== null && (
        <p className="text-danger mb-0">
          {intl.formatMessage(problem === 'noMailbox' ? messages.startNoMailbox : messages.startFailed)}
        </p>
      )}
      <StartBar
        campaign={campaign}
        fitNow={fitNow}
        hasMailbox={from.length > 0}
        readOnly={readOnly}
        isStarting={isChangingState}
        isEditing={isEditing}
        onBack={() => onStep('sendFrom')}
        onStart={isEditing ? save : start}
      />
    </>
  );
};

export const StepStart = TypedMemo(StepStartComponent);
